import { useState, useEffect, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Instagram, ExternalLink } from "@/components/icons";
import { Skeleton } from "@/components/ui/skeleton";

type PostConfig = {
  id: string;
  url: string;
};

type PostData = {
  id: string;
  url: string;
  html?: string;
  loading: boolean;
  error: boolean;
};

// Füge hier weitere Instagram-Post-URLs hinzu, um mehr Beiträge anzuzeigen.
// Die Reihenfolge bestimmt die Darstellung auf der Startseite.
const posts: PostConfig[] = [
  { id: "DOOmjeDDwa7", url: "https://www.instagram.com/p/DOOmjeDDwa7/" },
  { id: "DN3bZ5G0GGt", url: "https://www.instagram.com/p/DN3bZ5G0GGt/" },
  { id: "DNoMaUBtfjV", url: "https://www.instagram.com/p/DNoMaUBtfjV/" },
];

const INSTAGRAM_EMBED_SCRIPT = "https://platform.instagram.com/en_US/embeds.js";

const loadInstagramScript = (): Promise<void> => {
  return new Promise((resolve) => {
    if (document.querySelector(`script[src="${INSTAGRAM_EMBED_SCRIPT}"]`)) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = INSTAGRAM_EMBED_SCRIPT;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => resolve();
    document.body.appendChild(script);
  });
};

const processEmbeds = () => {
  if (typeof window !== "undefined" && (window as any).instgrm?.Embeds) {
    (window as any).instgrm.Embeds.process();
  }
};

const fetchOEmbed = async (
  url: string,
  signal?: AbortSignal
): Promise<string> => {
  const endpoint = `https://graph.facebook.com/v26.0/instagram_oembed?url=${encodeURIComponent(
    url
  )}`;
  const response = await fetch(endpoint, { signal });
  if (!response.ok) {
    throw new Error(`oEmbed request failed: ${response.status}`);
  }
  const data = await response.json();
  return data.html as string;
};

export const InstagramFeed = () => {
  const [postData, setPostData] = useState<PostData[]>(
    posts.map((p) => ({ ...p, loading: !!p.url, error: false }))
  );

  const sectionRef = useRef<HTMLElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return;
    const controllers: AbortController[] = [];

    const loadPosts = async () => {
      const results = await Promise.all(
        posts.map(async (post) => {
          if (!post.url) {
            return { ...post, loading: false, error: true };
          }

          const controller = new AbortController();
          controllers.push(controller);
          const timeoutId = setTimeout(() => controller.abort(), 8000);

          try {
            const html = await fetchOEmbed(post.url, controller.signal);
            clearTimeout(timeoutId);
            return { ...post, html, loading: false, error: false };
          } catch (error) {
            clearTimeout(timeoutId);
            return { ...post, html: undefined, loading: false, error: true };
          }
        })
      );

      setPostData(results);
    };

    loadPosts();

    return () => {
      controllers.forEach((controller) => controller.abort());
    };
  }, [inView]);

  useEffect(() => {
    if (postData.some((post) => post.html && !post.error)) {
      loadInstagramScript().then(() => {
        processEmbeds();
        window.setTimeout(() => {
          document
            .querySelectorAll<HTMLIFrameElement>("iframe.instagram-media:not([title])")
            .forEach((frame, index) => {
              frame.setAttribute(
                "title",
                `Instagram-Beitrag ${index + 1} des Werbekreis Roding`
              );
            });
        }, 1500);
      });
    }
  }, [postData]);

  const renderSkeleton = (key: string) => (
    <Card key={key} className="overflow-hidden h-full">
      <CardContent className="p-0 h-full flex flex-col">
        <Skeleton className="aspect-square w-full flex-shrink-0" />
        <div className="p-3 space-y-2 flex-1">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      </CardContent>
    </Card>
  );

  const renderFallback = (post: PostData) => (
    <a
      key={post.id}
      href={post.url || "https://www.instagram.com/werbekreisroding/"}
      target="_blank"
      rel="noopener noreferrer"
      className="block group h-full"
    >
      <Card className="overflow-hidden hover:shadow-lg transition-shadow h-full">
        <CardContent className="p-0 h-full">
          <div className="aspect-square bg-gradient-to-br from-primary/80 via-primary to-accent flex items-center justify-center">
            <div className="text-primary-foreground text-center p-6">
              <Instagram className="h-12 w-12 mx-auto mb-3 opacity-90 group-hover:scale-110 transition-transform" />
              <p className="text-sm font-medium">Beitrag ansehen</p>
              <span className="inline-flex items-center gap-1 mt-2 text-xs underline-offset-2 group-hover:underline">
                <ExternalLink className="h-3 w-3" />
                Auf Instagram öffnen
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </a>
  );

  const renderEmbed = (post: PostData, index: number) => (
    <a
      key={post.id}
      href={post.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Instagram-Beitrag ${index + 1} auf Instagram öffnen`}
      className="block group relative h-full"
    >
      <Card className="overflow-hidden hover:shadow-lg transition-shadow h-full">
        <CardContent className="p-0 h-full relative">
          <div
            className="aspect-square w-full overflow-hidden bg-background"
            dangerouslySetInnerHTML={{ __html: post.html || "" }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
          <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            <span className="inline-flex items-center gap-1 text-xs text-white bg-black/60 px-2 py-1 rounded">
              <ExternalLink className="h-3 w-3" />
              Öffnen
            </span>
          </div>
        </CardContent>
      </Card>
    </a>
  );

  const renderCard = (post: PostData, index: number) => {
    if (!inView || post.loading) {
      return renderSkeleton(post.id);
    }

    if (post.error || !post.html) {
      return renderFallback(post);
    }

    return renderEmbed(post, index);
  };

  return (
    <section ref={sectionRef} className="py-16 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Instagram className="h-8 w-8 text-primary" />
            <h2 className="text-3xl font-bold text-foreground">
              Folge uns auf Instagram
            </h2>
          </div>
          <p className="text-lg text-muted-foreground">
            Aktuelle Einblicke und Updates vom Werbekreis Roding
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {postData.map((post, index) => renderCard(post, index))}
        </div>

        <div className="text-center">
          <a
            href="https://www.instagram.com/werbekreisroding/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground font-semibold rounded-full hover:bg-primary/90 transition-all duration-200"
          >
            <Instagram className="h-5 w-5" />
            @werbekreisroding folgen
          </a>
        </div>
      </div>
    </section>
  );
};
